import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Container, Divider, Grid, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    CheckCircle as CheckCircleIcon,
    LocationOn as LocationOnIcon,
    NotInterested as NotInterestedIcon,
    OpenInNew,
    TextSnippet as TextSnippetIcon,
} from '@mui/icons-material';
import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { LoggedInUser } from '../../../types/auth.types';
import Anchor from '../foundation/Anchor';
import { BGContainer, WhiteBGContainer } from '../foundation/Containers';
import CustomBR from '../foundation/CustomBR';
import Page from '../layout/base.page';
import TextArea from '../foundation/TextArea';
import TextField from '../foundation/TextField';
import Button from '../foundation/Button';
import Checkbox from '../foundation/Checkbox';
import {
    CANDIDATES_SEARCH_PAGE_SIZE, CandidateRecord, searchAdminCandidatesAI,
    searchCandidatesAI, sendPromotedJobToCandidate,
} from '../../api/searchCandidates.api';
import { systemUserApplyToJob } from '../../api/userToJob.api';
import SimilarityGauge from '../components/SimilarityGauge';
import { GeoAutocomplete, LocationOption } from '../components/AutocompleteRemote';
import { CenteredFlex } from '../foundation/Flex';
import { ParsedCvJson } from '../../../dal/entities/parsedCv';
import { AppliedFilter } from '../../../types/aiCandidateSearch.types';
import {
    JOB_DESCRIPTION_MAX_LENGTH,
    JOB_DESCRIPTION_MIN_LENGTH,
} from '../../../common/jobValidation.constants';
import {
    EMBEDDING_SEARCH_MODEL_LABELS, EmbeddingSearchModel,
} from '../../../types/embeddingSearch.types';
import {
    ADMIN_AI_MATCH_REJECTION_FILTERS,
    AdminAiMatchRejectionFilter,
} from '../../../common/admin_job_cv_rejection_reason.enum';
import AdminAiMatchRejectionControl, {
    ADMIN_REJECTION_REASON_LABELS,
} from '../components/AdminAiMatchRejectionControl';
import CandidateSearchRequestGate from '../utils/CandidateSearchRequestGate';

const CandidateCard = styled(Card, {
    shouldForwardProp: prop => prop !== 'isRejected',
})<{ isRejected?: boolean }>(({ theme, isRejected }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: isRejected ? alpha(theme.palette.error.main, 0.08) : undefined,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
        boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
        transform: 'translateY(-2px)',
    },
}));

const getStripColor = (theme, similarity: number): string => {
    if (similarity > 0.7) return theme.palette.success.main;
    if (similarity > 0.5) return '#FFC400';
    return theme.palette.error.main;
};

const CardTopStrip = styled(Box)<{ similarity: number }>(({ theme, similarity }) => ({
    height: 5,
    borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
    background: getStripColor(theme, similarity),
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const SummaryText = styled(Typography)({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const CV_SECTION_LABELS: { key: keyof Omit<ParsedCvJson, 'user_details'>; label: string }[] = [
    { key: 'summary', label: 'סיכום' },
    { key: 'experience', label: 'ניסיון' },
    { key: 'education', label: 'השכלה' },
    { key: 'hard_skills', label: 'כישורים טכניים' },
    { key: 'soft_skills', label: 'כישורים רכים' },
    { key: 'languages', label: 'שפות' },
    { key: 'location', label: 'מיקום' },
    { key: 'extra', label: 'נוסף' },
];

export type AISearchCandidatesProps = {
    url: string
    title: string
    user?: LoggedInUser
    candidates?: CandidateRecord[]
    jobId?: string
    description?: string
    location?: string
    latitude?: number
    longitude?: number
    distance?: number
}

type IFormPostJob = {
    description: string
    distance?: number
    jobId?: number
    onlyShowToEmployers: boolean
}

const AISearchCandidates = (props: AISearchCandidatesProps) => {
    const [candidates, setCandidates] = React.useState<CandidateRecord[]>(props.candidates || []);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);
    const [appliedFilter, setAppliedFilter] = React.useState<AppliedFilter>('all');
    const [embeddingModel, setEmbeddingModel] = React.useState<EmbeddingSearchModel>('google');
    const [rejectionFilter, setRejectionFilter] = React.useState<AdminAiMatchRejectionFilter>(
        ADMIN_AI_MATCH_REJECTION_FILTERS.NOT_REJECTED,
    );
    const [activeSearchJobId, setActiveSearchJobId] = React.useState<number | null>(null);
    const [searchError, setSearchError] = React.useState('');
    const lastSearchPayloadRef = React.useRef<Parameters<typeof searchCandidatesAI>[0] | null>(null);
    const searchGate = React.useRef(new CandidateSearchRequestGate());
    const [isSearching, setIsSearching] = React.useState(false);
    const applyingUsers = React.useRef(new Set<number>());
    const [applyingUserIds, setApplyingUserIds] = React.useState<number[]>([]);

    const invalidateSearch = React.useCallback(() => {
        searchGate.current.invalidate();
        setIsSearching(false);
        setCandidates([]);
        setHasMore(false);
        setCurrentPage(1);
        setActiveSearchJobId(null);
        setSearchError('');
        lastSearchPayloadRef.current = null;
    }, []);

    const [location, setLocation] = React.useState<LocationOption | null>(
        props.latitude && props.longitude
            ? { formatted_address: props.location || '', latitude: props.latitude, longitude: props.longitude }
            : null,
    );

    const {
        register, handleSubmit, formState: { errors }, watch,
    } = useForm<IFormPostJob>({
        defaultValues: {
            description: props.description,
            distance: props.distance ?? 20,
            jobId: props.jobId ? Number(props.jobId) : undefined,
            onlyShowToEmployers: true,
        },
    });

    const currentJobId = watch('jobId');
    React.useEffect(() => {
        const subscription = watch((_values, { name }) => {
            if (name) invalidateSearch();
        });
        return () => subscription.unsubscribe();
    }, [watch, invalidateSearch]);

    React.useEffect(() => () => searchGate.current.invalidate(), []);
    let jobIdHelperText = '';
    if (errors.jobId) {
        jobIdHelperText = embeddingModel === 'mploy_v1'
            ? 'יש להזין מספר משרה לצורך חיפוש בקידוד Mploy'
            : 'מספר משרה לא תקין';
    }

    React.useEffect(() => {
        if (!currentJobId && appliedFilter !== 'all') {
            setAppliedFilter('all');
            setCandidates([]);
            setHasMore(false);
            setCurrentPage(1);
            setActiveSearchJobId(null);
            lastSearchPayloadRef.current = null;
        }
    }, [currentJobId, appliedFilter]);

    const onLocationChange = (_event: React.SyntheticEvent, value: LocationOption) => {
        invalidateSearch();
        if (value?.latitude && value?.longitude) {
            setLocation(value);
        } else {
            setLocation(null);
        }
    };

    const runSearch = async (
        payload: Parameters<typeof searchCandidatesAI>[0],
        page: number,
        append = false,
        requestedRejectionFilter = rejectionFilter,
    ) => {
        if (applyingUsers.current.size) return;
        const request = searchGate.current.begin();
        if (!request) return;
        setIsSearching(true);
        setSearchError('');
        if (!append) {
            setCandidates([]);
            setActiveSearchJobId(null);
            setHasMore(false);
        }
        try {
            const searchPayload = { ...payload, page, pageSize: CANDIDATES_SEARCH_PAGE_SIZE };
            const res = props.user?.admin
                ? await searchAdminCandidatesAI({
                    ...searchPayload,
                    offset: append ? candidates.length : 0,
                    rejectionFilter: requestedRejectionFilter,
                }, false, request.signal)
                : await searchCandidatesAI(searchPayload, false, request.signal);
            if (res && searchGate.current.isCurrent(request.version)) {
                setCandidates(prev => {
                    if (!append) return res.data.candidates;
                    const existing = new Set(prev.map(candidate => candidate.cvId));
                    return [...prev, ...res.data.candidates.filter(candidate => !existing.has(candidate.cvId))];
                });
                setHasMore(res.data.hasMore);
                setCurrentPage(page);
                if (!append) setActiveSearchJobId(payload.jobId ?? null);
            }
        } catch (err: any) {
            if (!searchGate.current.isCurrent(request.version) || request.signal.aborted) return;
            const responseData = err?.response?.data;
            const message = (err?.response?.status === 400 && typeof responseData === 'string' && responseData)
                || responseData?.message
                || 'החיפוש לא הושלם. אפשר לנסות שוב או לצמצם את תנאי החיפוש.';
            setSearchError(message);
        } finally {
            if (searchGate.current.finish(request.version)) setIsSearching(false);
        }
    };

    const onEmbeddingModelChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: EmbeddingSearchModel | null,
    ) => {
        if (!value) return;
        invalidateSearch();
        setEmbeddingModel(value);
    };

    const onSubmit = async (data: IFormPostJob) => {
        if (searchGate.current.pending || applyingUsers.current.size) return;
        const payload = {
            jobDescription: data.description || '',
            latitude: location?.latitude,
            longitude: location?.longitude,
            distance: data.distance ? Number(data.distance) : undefined,
            onlyShowToEmployers: data.onlyShowToEmployers,
            jobId: data.jobId ? Number(data.jobId) : undefined,
            appliedFilter,
            embeddingModel,
        };
        lastSearchPayloadRef.current = payload;
        await runSearch(payload, 1);
    };

    const onLoadMore = async () => {
        if (!lastSearchPayloadRef.current) return;
        await runSearch(lastSearchPayloadRef.current, currentPage + 1, true);
    };

    const onAppliedFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextAppliedFilter = e.target.value as AppliedFilter;
        invalidateSearch();
        setAppliedFilter(nextAppliedFilter);
    };

    const onRejectionFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const nextRejectionFilter = event.target.value as AdminAiMatchRejectionFilter;
        invalidateSearch();
        setRejectionFilter(nextRejectionFilter);
    };

    const descriptionInput = register('description', {
        required: embeddingModel === 'google',
        minLength: embeddingModel === 'google' ? JOB_DESCRIPTION_MIN_LENGTH : undefined,
        maxLength: embeddingModel === 'google' ? JOB_DESCRIPTION_MAX_LENGTH : undefined,
    });
    const distanceInput = register('distance', { min: 1, max: 200 });
    const jobIdInput = register('jobId', { min: 1, required: embeddingModel === 'mploy_v1' });
    const onlyShowToEmployersInput = register('onlyShowToEmployers');

    const postCandidateToJob = async (candidate: CandidateRecord) => {
        if (!activeSearchJobId || searchGate.current.pending || applyingUsers.current.has(candidate.userId)) return;
        const jobId = activeSearchJobId;
        const { version } = searchGate.current;
        applyingUsers.current.add(candidate.userId);
        setApplyingUserIds([...applyingUsers.current]);
        try {
            await systemUserApplyToJob({ userid: candidate.userId, jobid: jobId, cvid: candidate.cvId });
            if (!searchGate.current.isCurrent(version)) return;
            setCandidates(prev => {
                if (appliedFilter === 'notApplied') return prev.filter(c => c.userId !== candidate.userId);
                return prev.map(c => (c.userId === candidate.userId ? { ...c, alreadyApplied: true } : c));
            });
            window.showGlobalModal('הגשת מועמדות', `המועמד ${candidate.firstName} הופנה בהצלחה למעסיק השייך למשרה ${jobId}`);
        } catch {
            if (searchGate.current.isCurrent(version)) window.showGlobalModal('שגיאה בהגשת מועמדות', 'הפעולה לא הושלמה. יש לבדוק את מצב ההגשה לפני ניסיון נוסף.');
        } finally {
            applyingUsers.current.delete(candidate.userId);
            setApplyingUserIds([...applyingUsers.current]);
        }
    };

    const sendJobToCandidate = async (candidate: CandidateRecord) => {
        try {
            await sendPromotedJobToCandidate({ userId: candidate.userId, jobId: Number(activeSearchJobId) });
            window.showGlobalModal('שליחת משרה חמה', `המשרה ${activeSearchJobId} נשלחה בהצלחה למועמד ${candidate.userId}`);
        } catch (error) {
            window.showGlobalModal('שגיאה בשליחת משרה', `${error.toString()} | ${error.response?.data}`);
        }
    };

    const renderCandidateCard = (candidate: CandidateRecord) => {
        const { version } = searchGate.current;
        const lastActivityStr = candidate.lastActivity
            ? new Date(candidate.lastActivity).toLocaleDateString('he-IL')
            : null;
        const locationsStr = candidate.locations?.filter(Boolean).join(' · ');

        return (
            <CandidateCard
                key={`${candidate.userId}-${candidate.cvId}`}
                elevation={2}
                isRejected={!!candidate.adminRejection}
            >
                <CardTopStrip similarity={candidate.similarity} />
                <CardContent>
                    <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            {/* Name + consent chip */}
                            <Box style={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px',
                            }}>
                                <Typography variant="h6" fontWeight="bold" noWrap>
                                    {candidate.firstName}
                                </Typography>
                                <Chip
                                    size="small"
                                    icon={candidate.showToEmployers
                                        ? <CheckCircleIcon style={{ fontSize: 14 }} />
                                        : <NotInterestedIcon style={{ fontSize: 14 }} />}
                                    label={candidate.showToEmployers ? 'אישר שיתוף פרופיל' : 'לא אישר שיתוף'}
                                    color={candidate.showToEmployers ? 'success' : 'default'}
                                />
                                {candidate.alreadyApplied && (
                                    <Chip
                                        size="small"
                                        icon={<AssignmentTurnedInIcon style={{ fontSize: 14 }} />}
                                        label="הוגש"
                                        color="primary"
                                    />
                                )}
                                {candidate.adminRejection && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={`נפסלה: ${ADMIN_REJECTION_REASON_LABELS[candidate.adminRejection.reasonCode]}`}
                                    />
                                )}
                            </Box>

                            {/* Meta rows */}
                            {locationsStr && (
                                <MetaRow>
                                    <LocationOnIcon fontSize="small" />
                                    <Typography variant="body2">{locationsStr}</Typography>
                                </MetaRow>
                            )}
                            {lastActivityStr && (
                                <MetaRow>
                                    <AccessTimeIcon fontSize="small" />
                                    <Typography variant="body2">פעילות אחרונה: {lastActivityStr}</Typography>
                                </MetaRow>
                            )}
                            {candidate.cvSummary && (
                                <MetaRow style={{ marginTop: '8px', alignItems: 'flex-start' }}>
                                    <TextSnippetIcon fontSize="small" style={{ marginTop: 2 }} />
                                    <SummaryText variant="body2">{candidate.cvSummary}</SummaryText>
                                </MetaRow>
                            )}
                            {candidate.location && (
                                <MetaRow>
                                    <LocationOnIcon fontSize="small" />
                                    <Typography variant="body2">{candidate.location}</Typography>
                                </MetaRow>
                            )}
                        </Box>

                        {/* Right: gauge */}
                        <Box style={{ flexShrink: 0 }}>
                            <SimilarityGauge similarity={candidate.similarity} size="medium" />
                            <Typography variant="caption" color="text.secondary" display="block">
                                ציון מיון: {(candidate.rankingScore ?? candidate.similarity).toFixed(3)}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Actions */}
                    <Box style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}>
                        <Box style={{
                            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                        }}>
                            <Anchor
                                blank
                                href={`/anonymous-employer-view/watchcv/${candidate.userId}/cv/${candidate.cvId}`}
                            >
                                <CenteredFlex><OpenInNew fontSize="small" /> צפייה בקורות חיים </CenteredFlex>
                            </Anchor>

                            <Divider orientation="vertical" flexItem />

                            <Button
                                size="small"
                                variant="outlined"
                                disabled={isSearching || applyingUserIds.includes(candidate.userId) || !activeSearchJobId || candidate.alreadyApplied || !!candidate.adminRejection}
                                onClick={() => postCandidateToJob(candidate)}
                            >
                                שליחת מועמד למשרה
                            </Button>

                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                disabled={isSearching || !activeSearchJobId || !!candidate.adminRejection}
                                onClick={() => sendJobToCandidate(candidate)}
                            >
                                שליחת משרה חמה למועמד
                            </Button>
                        </Box>

                        {props.user?.admin && activeSearchJobId && !isSearching && (
                            <AdminAiMatchRejectionControl
                                jobId={activeSearchJobId}
                                cvId={candidate.cvId}
                                isRejected={!!candidate.adminRejection}
                                onRejected={rejection => setCandidates(prev => {
                                    if (!searchGate.current.isCurrent(version)) return prev;
                                    return rejectionFilter === ADMIN_AI_MATCH_REJECTION_FILTERS.ALL
                                        ? prev.map(item => (item.cvId === candidate.cvId ? {
                                            ...item,
                                            adminRejection: {
                                                reasonCode: rejection.reasonCode,
                                                createdAt: rejection.createdAt,
                                            },
                                        } : item))
                                        : prev.filter(item => item.cvId !== candidate.cvId);
                                })}
                                onRejectionRemoved={() => setCandidates(prev => {
                                    if (!searchGate.current.isCurrent(version)) return prev;
                                    return rejectionFilter === ADMIN_AI_MATCH_REJECTION_FILTERS.REJECTED
                                        ? prev.filter(item => item.cvId !== candidate.cvId)
                                        : prev.map(item => (item.cvId === candidate.cvId ? {
                                            ...item,
                                            adminRejection: undefined,
                                        } : item));
                                })}
                            />
                        )}
                    </Box>

                    {/* Parsed CV text for AI scanning */}
                    {candidate.parsedCv ? (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            <Box style={{ fontSize: '12px', color: '#555', lineHeight: 1.6 }}>
                                {CV_SECTION_LABELS.map(({ key, label }) => candidate.parsedCv![key] && (
                                    <Box key={key} style={{ marginBottom: '6px' }}>
                                        <Typography variant="caption" fontWeight="bold">{label}: </Typography>
                                        <Typography variant="caption">{candidate.parsedCv![key] as string}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            <Anchor
                                blank
                                href={`/anonymous-employer-view/watchcv/${candidate.userId}/cv/${candidate.cvId}`}
                            >
                                <Typography variant="caption">קורות חיים — צפייה בקובץ</Typography>
                            </Anchor>
                        </>
                    )}
                </CardContent>
            </CandidateCard>
        );
    };

    return <Page
        showBreadCrumbs
        url={props.url}
        user={props.user}
        pageHeaderTitle={`Mploy - ${props.title}`} >
        <Container>
            <CustomBR />
            <BGContainer>
                <Typography color="secondary" fontWeight="bold" variant="h4">{props.title}</Typography>
                <CustomBR />
                <CustomBR />
                <Box>
                    <Typography variant="body2" color="text.secondary" style={{ marginBottom: 6 }}>
                        מודל קידוד
                    </Typography>
                    <ToggleButtonGroup
                        value={embeddingModel}
                        exclusive
                        size="small"
                        onChange={onEmbeddingModelChange}
                    >
                        {(Object.keys(EMBEDDING_SEARCH_MODEL_LABELS) as EmbeddingSearchModel[])
                            .map(value => (
                                <ToggleButton key={value} value={value}>
                                    {EMBEDDING_SEARCH_MODEL_LABELS[value]}
                                </ToggleButton>
                            ))}
                    </ToggleButtonGroup>
                    {embeddingModel === 'mploy_v1' && (
                        <Alert severity="info" style={{ marginTop: 8 }}>
                            קידוד Mploy משתמש במשרה השמורה לפי מספר המשרה; תיאור חופשי אינו נשלח למודל זה.
                        </Alert>
                    )}
                    <CustomBR />
                </Box>
                <Box>
                    <Typography>הזן את תיאור המשרה</Typography>
                    <CustomBR />
                    <TextArea
                        // defaultValue={props.job?.description}
                        {...descriptionInput}
                        inputRef={descriptionInput.ref}
                        required={embeddingModel === 'google'}
                        disabled={embeddingModel === 'mploy_v1'}
                        // onBlur={() => {}}
                        label="תיאור המשרה"
                        name="description"
                        id="description"
                        helperText={errors.description
                            && `יש להזין תיאור משרה בין ${JOB_DESCRIPTION_MIN_LENGTH} ל ${JOB_DESCRIPTION_MAX_LENGTH} תווים.`}
                        fullWidth
                        rows={5}
                        maxLength={JOB_DESCRIPTION_MAX_LENGTH}
                    />
                </Box>
                <CustomBR />
                <Typography>סינון לפי מיקום</Typography>
                <CustomBR />
                <Grid container spacing={2}>
                    <Grid item md={3} xs={12}>
                        <GeoAutocomplete
                            defaultValue={props.location}
                            onChange={onLocationChange}
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <TextField
                            {...distanceInput}
                            inputRef={distanceInput.ref}
                            label="מרחק (ק״מ)"
                            type="number"
                            fullWidth
                            disabled={!location}
                            inputProps={{ min: 1, max: 200 }}
                            helperText={errors.distance ? 'יש להזין מרחק בין 1 ל-200 ק״מ' : ''}
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <TextField
                            {...jobIdInput}
                            inputRef={jobIdInput.ref}
                            label="מספר משרה"
                            type="number"
                            fullWidth
                            inputProps={{ min: 1 }}
                            helperText={jobIdHelperText}
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <TextField
                            select
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                            label="סטטוס הגשה למשרה"
                            fullWidth
                            disabled={!currentJobId}
                            value={appliedFilter}
                            onChange={onAppliedFilterChange}
                        >
                            <option value="all">הכל</option>
                            <option value="applied">הוגש</option>
                            <option value="notApplied">לא הוגש</option>
                        </TextField>
                    </Grid>
                    {props.user?.admin && (currentJobId || activeSearchJobId) && (
                        <Grid item md={3} xs={12}>
                            <TextField
                                select
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                                label="סטטוס שלילה למשרה"
                                fullWidth
                                value={rejectionFilter}
                                onChange={onRejectionFilterChange}
                            >
                                <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.ALL}>הכל</option>
                                <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.REJECTED}>נפסלו</option>
                                <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.NOT_REJECTED}>לא נפסלו</option>
                            </TextField>
                        </Grid>
                    )}
                </Grid>
                <CustomBR />
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        {...onlyShowToEmployersInput}
                        inputRef={onlyShowToEmployersInput.ref}
                        defaultChecked
                    />
                    <Typography>הצג רק מועמדים שאישרו שיתוף פרופיל עם מעסיקים</Typography>
                </Box>
                <CustomBR />

                <Button size="large" onClick={handleSubmit(onSubmit)} color="primary" fullWidth disabled={isSearching || applyingUserIds.length > 0} aria-busy={isSearching}>
                    {isSearching ? 'מחפש מועמדים…' : 'הצג מועמדים רלוונטים'}
                </Button>
                <Typography variant="caption">שינוי תנאי החיפוש ייכנס לתוקף בלחיצה על חיפוש.</Typography>
                {searchError && (
                    <Box style={{ marginTop: 16 }}>
                        <Alert severity="error" onClose={() => setSearchError('')}>
                            {searchError}
                        </Alert>
                    </Box>
                )}
            </BGContainer>

            <CustomBR />
            {candidates?.length ? <WhiteBGContainer>
                <div>
                    <Typography color="secondary" fontWeight="bold" variant="h4">
                        מועמדים רלוונטים{activeSearchJobId ? ` למשרה ${activeSearchJobId}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        מודל: {EMBEDDING_SEARCH_MODEL_LABELS[embeddingModel]}
                    </Typography>
                    <CustomBR />
                    <CustomBR />
                    {candidates.map(renderCandidateCard)}

                </div>
            </WhiteBGContainer> : ''}

            {hasMore && (
                <Button fullWidth variant="outlined" size="large" onClick={onLoadMore} disabled={isSearching || applyingUserIds.length > 0} aria-busy={isSearching}>
                    {isSearching ? 'טוען מועמדים…' : 'טען עוד מועמדים'}
                </Button>
            )}
        </Container>
        <CustomBR />
    </Page>;
};
export default AISearchCandidates;
